import { toast } from '@/components/ui/Toast';
import { certificationPerformanceService } from '@/services/certification-performance.service';
import { userService } from '@/services/user.service';
import type {
  ExamAttemptDetailed,
  ExamQuestion,
  ExamAnswer
} from '@/types/certification-performance.types';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Award,
  BookOpen,
  Briefcase,
  Building,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit2,
  FileText,
  Save,
  Search,
  Target,
  Trophy,
  User,
  XCircle,
} from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SimpleSelect from '@/components/ui/SimpleSelect';

const filterStatusOptions = [
  { label: 'All Status', value: 'all' },
  { label: 'Passed', value: 'passed' },
  { label: 'Failed', value: 'failed' },
];

const sortByOptions = [
  { label: 'Latest First', value: 'attempt_number' },
  { label: 'Highest Score', value: 'score' },
  { label: 'Best Percentage', value: 'percentage' },
  { label: 'Time Spent', value: 'time_spent' },
  { label: 'Most Recent', value: 'started_at' },
];

interface UserInfo {
  name: string;
  _id: string;
  given_name: string;
  family_name: string;
  email: string;
  profile_image?: string;
  department?: string;
  departments?: string[];
  role?: string;
  roles?: string[];
  status: string;
  joinedDate?: string;
  lastActive?: string;
}

interface CertificationInfo {
  title: string;
  certification_id: string;
  category?: string;
  difficulty?: string;
  total_attempts?: number;
  passing_score?: number;
  time_limit_minutes?: number;
  deadline?: string;
  deadlineRaw?: string;
}

export default function UserCertificationProgress() {
  const { userId, certificationId } = useParams<{ userId: string; certificationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [certificationInfo, setCertificationInfo] = useState<CertificationInfo | null>(null);
  const [examAttempts, setExamAttempts] = useState<ExamAttemptDetailed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('attempt_number');
  const [expandedAttempts, setExpandedAttempts] = useState<string[]>([]);
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const TITLE_CHAR_LIMIT = 30;
  const [trackingSettings, setTrackingSettings] = useState<{
    trackingId: string | null;
    is_mandatory: boolean;
    deadline_date: string | null;
    send_notification: boolean;
    enable_automatic_reminder: boolean;
    reminder_frequency_days: number | null;
  }>(() => {
    const s = location.state as any;
    return {
      trackingId: s?.trackingId || null,
      is_mandatory: s?.isMandatory ?? false,
      deadline_date: s?.deadlineDate || null,
      send_notification: s?.sendNotification ?? false,
      enable_automatic_reminder: s?.enableAutomaticReminder ?? false,
      reminder_frequency_days: s?.reminderFrequencyDays ?? null,
    };
  });
  const [editForm, setEditForm] = useState({
    is_mandatory: false,
    deadline_date: '',
    send_notification: false,
    enable_automatic_reminder: false,
    reminder_frequency_days: '' as any,
  });

  useEffect(() => {
    if (userId && certificationId) {
      fetchData();
    }
  }, [userId, certificationId]);

  // Handle dynamic back navigation
  const handleBackNavigation = () => {
    navigate(-1)
  };

  const handleRevokeCertification = async () => {
    if (!certificationId || !userId) return;
    setRevokeLoading(true);
    try {
      const result = await certificationPerformanceService.revokeCertificationForUser(certificationId, userId);
      toast.success(
        `Certification revoked. Deleted: ${result.certification_trackings_deleted} tracking(s), ${result.exam_attempts_deleted} exam attempt(s), ${result.certificates_deleted} certificate(s).`
      );
      setShowRevokeModal(false);
      navigate(-1);
    } catch (err: any) {
      const message = err?.response?.data?.error?.error_description || err?.response?.data?.description || err?.message || 'Failed to revoke certification';
      toast.error(message);
    } finally {
      setRevokeLoading(false);
    }
  };

  const openEditModal = () => {
    const deadlineRaw = trackingSettings.deadline_date;
    setEditForm({
      is_mandatory: trackingSettings.is_mandatory,
      deadline_date: deadlineRaw ? new Date(deadlineRaw).toISOString().split('T')[0] : '',
      send_notification: trackingSettings.send_notification,
      enable_automatic_reminder: trackingSettings.enable_automatic_reminder,
      reminder_frequency_days: trackingSettings.reminder_frequency_days || '',
    });
    setShowEditModal(true);
  };

  const handleSaveSettings = async () => {
    const trackingId = trackingSettings.trackingId;
    if (!trackingId) {
      toast.error('Tracking ID not available');
      return;
    }

    if (editForm.enable_automatic_reminder) {
      const freq = Number(editForm.reminder_frequency_days);
      if (!freq || freq <= 0) {
        toast.error('Reminder frequency must be greater than 0');
        return;
      }
      if (freq > 100) {
        toast.error('Reminder frequency cannot exceed 100 days');
        return;
      }
    }

    // Only validate past-date when the admin actually changed the deadline.
    // Editing other fields on an already-overdue assignment stays unblocked.
    if (editForm.deadline_date) {
      const originalDeadline = trackingSettings.deadline_date
        ? new Date(trackingSettings.deadline_date).toISOString().split('T')[0]
        : '';
      const deadlineChanged = editForm.deadline_date !== originalDeadline;

      if (deadlineChanged) {
        const deadline = new Date(editForm.deadline_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (deadline < today) {
          toast.error('Deadline must be today or a future date');
          return;
        }
      }
    }

    const saved_is_mandatory = editForm.is_mandatory;
    const saved_deadline = editForm.deadline_date || null;
    const saved_send_notification = editForm.send_notification;
    const saved_enable_automatic_reminder = saved_send_notification ? editForm.enable_automatic_reminder : false;
    const saved_reminder_frequency_days = saved_send_notification && saved_enable_automatic_reminder && editForm.reminder_frequency_days ? Number(editForm.reminder_frequency_days) : null;

    setEditLoading(true);
    try {
      await certificationPerformanceService.updateTrackingSettings(trackingId, {
        is_mandatory: saved_is_mandatory,
        deadline_date: saved_deadline || undefined,
        send_notification: saved_send_notification,
        enable_automatic_reminder: saved_enable_automatic_reminder,
        reminder_frequency_days: saved_reminder_frequency_days,
      });

      // Update certificationInfo (drives deadline in header) optimistically
      setCertificationInfo(prev => prev ? ({
        ...prev,
        deadline: saved_deadline ? formatDate(saved_deadline) : undefined,
        deadlineRaw: saved_deadline || undefined,
      }) : prev);

      // Update local state so the edit modal reflects saved values immediately
      setTrackingSettings(prev => ({
        ...prev,
        is_mandatory: saved_is_mandatory,
        deadline_date: saved_deadline,
        send_notification: saved_send_notification,
        enable_automatic_reminder: saved_enable_automatic_reminder,
        reminder_frequency_days: saved_reminder_frequency_days,
      }));

      toast.success('Tracking settings updated successfully');
      setShowEditModal(false);
    } catch (err: any) {
      const message = err?.response?.data?.error?.error_description || err?.message || 'Failed to update settings';
      toast.error(message);
    } finally {
      setEditLoading(false);
    }
  };

  const fetchData = async () => {
    if (!userId || !certificationId) {
      console.error('Missing userId or certificationId:', { userId, certificationId });
      return;
    }

    console.log('Fetching data for user:', userId, 'certification:', certificationId);
    setLoading(true);
    setError(null);

    // Get deadline from tracking settings state (kept in sync after edits)
    const deadlineDateFromState = trackingSettings.deadline_date;
    const certificationTitleFromState = (location.state as any)?.certificationTitle;

    try {
      const [userResponse, attemptsResponse] = await Promise.all([
        userService.getUserById(userId),
        certificationPerformanceService.getUserExamAttempts(userId, certificationId)
      ]);

      console.log('User response:', userResponse);
      console.log('Exam attempts response:', attemptsResponse);

      if (userResponse?.data) {
        const userData = userResponse.data;
        setUserInfo({
          ...userData,
          joinedDate: userData.joinedDate ? formatDate(userData.joinedDate) : undefined,
          lastActive: userData.lastActive ? formatDate(userData.lastActive) : undefined,
        });
      } else {
        console.warn('No user data received');
      }

      if (attemptsResponse) {
        console.log(`Setting ${attemptsResponse.length} exam attempts`);
        setExamAttempts(attemptsResponse);

        // Set certification info from attempts and state data
        setCertificationInfo({
          title: certificationTitleFromState || 'Certification Exam',
          certification_id: certificationId || '',
          total_attempts: attemptsResponse.length,
          deadline: deadlineDateFromState ? formatDate(deadlineDateFromState) : undefined,
          deadlineRaw: deadlineDateFromState || undefined
        });
      } else {
        console.warn('No exam attempts data received');
        setExamAttempts([]);
        // Still set certification info from state
        setCertificationInfo({
          title: certificationTitleFromState || 'Certification Exam',
          certification_id: certificationId || '',
          total_attempts: 0,
          deadline: deadlineDateFromState ? formatDate(deadlineDateFromState) : undefined,
          deadlineRaw: deadlineDateFromState || undefined
        });
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.description || err.message || 'Failed to load exam attempts');
    } finally {
      setLoading(false);
    }
  };

  const toggleAttemptExpansion = (attemptId: string) => {
    setExpandedAttempts(prev =>
      prev.includes(attemptId)
        ? prev.filter(id => id !== attemptId)
        : [...prev, attemptId]
    );
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  };

  const formatAnswer = (answer: any): string => {
    if (typeof answer === 'boolean') {
      return answer ? 'True' : 'False';
    }
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    if (typeof answer === 'string') {
      return answer;
    }
    return String(answer);
  };

  const calculateDaysUntilDeadline = (deadlineRaw?: string): number | undefined => {
    if (!deadlineRaw) return undefined;
    const deadline = new Date(deadlineRaw);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadline.setHours(0, 0, 0, 0);
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getQuestionAnswer = (question: ExamQuestion, answers: ExamAnswer[]) => {
    return answers.find(a => a.question_id === question.question_id);
  };

  // Filter and sort attempts
  const filteredAttempts = examAttempts.filter(attempt => {
    if (filterStatus !== 'all') {
      if (filterStatus === 'passed' && !attempt.passed) return false;
      if (filterStatus === 'failed' && attempt.passed) return false;
    }

    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const attemptStr = `attempt ${attempt.attempt_number}`.toLowerCase();
      const scoreStr = attempt.score.toString();
      if (!attemptStr.includes(searchLower) && !scoreStr.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  const sortedAttempts = [...filteredAttempts].sort((a, b) => {
    switch (sortBy) {
      case 'attempt_number':
        return b.attempt_number - a.attempt_number;
      case 'score':
        return b.score - a.score;
      case 'percentage':
        return b.percentage - a.percentage;
      case 'time_spent':
        return b.time_spent_seconds - a.time_spent_seconds;
      case 'started_at':
        return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
      default:
        return 0;
    }
  });

  // Calculate statistics
  const stats = {
    total: examAttempts.length,
    passed: examAttempts.filter(a => a.passed).length,
    failed: examAttempts.filter(a => !a.passed).length,
    averageScore: examAttempts.length > 0
      ? examAttempts.reduce((sum, a) => sum + a.percentage, 0) / examAttempts.length
      : 0,
    totalTime: examAttempts.reduce((sum, a) => sum + a.time_spent_seconds, 0),
    overallTimeSpent: examAttempts.reduce((sum, a) => sum + (a.overall_time_spent_seconds || 0), 0),
    bestScore: examAttempts.length > 0 ? Math.max(...examAttempts.map(a => a.percentage)) : 0
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              <div>
                <div className="h-7 w-72 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 w-56 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>

          {/* User and Certification Info Cards Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* User Info Card Skeleton */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-40 bg-gray-200 rounded"></div>
                  <div className="h-4 w-52 bg-gray-200 rounded"></div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Certification Info Card Skeleton */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-44 bg-gray-200 rounded"></div>
                  <div className="h-4 w-36 bg-gray-200 rounded"></div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="h-4 w-28 bg-gray-200 rounded"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="h-8 w-16 bg-gray-200 rounded mb-3"></div>
                <div className="h-2 w-full bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Exam Attempts List Skeleton */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 w-64 bg-gray-200 rounded"></div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-10 w-40 bg-gray-200 rounded-lg"></div>
                <div className="h-10 w-32 bg-gray-200 rounded-lg"></div>
                <div className="h-10 w-36 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-5 w-24 bg-gray-200 rounded"></div>
                        <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
                        <div className="h-6 w-12 bg-gray-200 rounded"></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-3 w-20 bg-gray-200 rounded"></div>
                        <div className="h-3 w-32 bg-gray-200 rounded"></div>
                        <div className="h-3 w-24 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-900 font-medium mb-1">Failed to Load</p>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackNavigation}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Individual Certification Progress</h1>
                <p className="text-sm text-gray-500 mt-1">Detailed exam attempts and progress tracking for {userInfo?.name}</p>
              </div>
            </div>
            <div className="flex items-center flex-shrink-0 gap-2">
              <button
                onClick={openEditModal}
                className="h-9 sm:h-10 px-3 sm:px-4 min-w-[90px] sm:min-w-[180px] bg-brand-indigo-600 text-white text-xs sm:text-sm rounded-lg hover:bg-brand-indigo-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Edit Settings</span>
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={() => setShowRevokeModal(true)}
                className="h-9 sm:h-10 px-3 sm:px-4 min-w-[90px] sm:min-w-[180px] bg-red-600 text-white text-xs sm:text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
              >
                <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Revoke Certification</span>
                <span className="sm:hidden">Revoke</span>
              </button>
            </div>
          </div>

          {/* User and Certification Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* User Info Card */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-brand-blue-100 flex items-center justify-center">
                  {userInfo?.profile_image ? (
                    <img src={userInfo.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-brand-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{userInfo?.name}</h3>
                  <p className="text-sm text-gray-500">{userInfo?.email}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                    {/* Departments */}
                    {(() => {
                      const departments = userInfo?.departments || (userInfo?.department ? [userInfo.department] : []);
                      if (departments.length === 0) {
                        return null;
                      }

                      if (departments.length === 1) {
                        return (
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                              {departments[0]}
                            </span>
                          </span>
                        );
                      }

                      return (
                        <span className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                            {departments[0]}
                          </span>
                          <button
                            onClick={() => setShowDepartmentsModal(true)}
                            className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline"
                          >
                            +{departments.length - 1} more
                          </button>
                        </span>
                      );
                    })()}

                    {/* Roles */}
                    {(() => {
                      const roles = userInfo?.roles || (userInfo?.role ? [userInfo.role] : []);

                      if (roles.length === 0) {
                        return null;
                      }

                      if (roles.length === 1) {
                        return (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                              {roles[0]}
                            </span>
                          </span>
                        );
                      }

                      return (
                        <span className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-gray-400" />
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                            {roles[0]}
                          </span>
                          <button
                            onClick={() => setShowRolesModal(true)}
                            className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline"
                          >
                            +{roles.length - 1} more
                          </button>
                        </span>
                        
                      );
                    })()}

                    {/* Status Badge */}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      userInfo?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {userInfo?.status}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div><span className="text-gray-500">Joined:</span> <span className="font-medium">{userInfo?.joinedDate || 'N/A'}</span></div>
                    <div><span className="text-gray-500">Last Active:</span> <span className="font-medium">{userInfo?.lastActive || 'N/A'}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Certification Info Card */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg bg-brand-purple-100 flex items-center justify-center">
                  <Award className="w-8 h-8 text-brand-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">
                      {(() => {
                        const t = certificationInfo?.title || 'Certification Exam';
                        if (t.length > TITLE_CHAR_LIMIT) {
                          return (
                            <>
                              {t.slice(0, TITLE_CHAR_LIMIT)}...
                              <button
                                onClick={() => setShowTitleModal(true)}
                                className="ml-1 text-xs font-medium text-brand-blue-600 hover:text-brand-blue-700 hover:underline"
                              >
                                show more
                              </button>
                            </>
                          );
                        }
                        return t;
                      })()}
                    </h3>
                    {trackingSettings.is_mandatory ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Mandatory
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-slate-50 to-gray-50 text-slate-600 border border-slate-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        Optional
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Certification Assessment</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div><span className="text-gray-500">Total Attempts:</span> <span className="font-medium">{stats.total}</span></div>
                    <div><span className="text-gray-500">Best Score:</span> <span className="font-medium">{Math.round(stats.bestScore)}%</span></div>
                    {certificationInfo?.deadline && (() => {
                      const daysUntilDeadline = calculateDaysUntilDeadline(certificationInfo.deadlineRaw);
                      const isTomorrow = daysUntilDeadline === 1;
                      const colorClass = isTomorrow ? 'text-red-600' : 'text-blue-600';
                      return (
                      <div className="col-span-2">
                        <span className="text-gray-500">Deadline:</span>{' '}
                        <span className={`font-medium ${colorClass}`}>{certificationInfo.deadline}</span>
                        {daysUntilDeadline !== undefined && daysUntilDeadline > 0 && (
                          <span className={`ml-2 ${colorClass}`}>({daysUntilDeadline} {daysUntilDeadline === 1 ? 'day' : 'days'} remaining)</span>
                        )}
                        {daysUntilDeadline !== undefined && daysUntilDeadline === 0 && (
                          <span className="text-orange-500 ml-2">(Due today)</span>
                        )}
                        {daysUntilDeadline !== undefined && daysUntilDeadline < 0 && (
                          <span className="text-red-500 ml-2">(Overdue)</span>
                        )}
                      </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Attempts Card */}
            <div className="bg-gradient-to-br from-brand-blue-50 to-brand-indigo-50 border border-brand-blue-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-brand-blue-100 rounded-full opacity-30 -translate-y-8 translate-x-8"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-brand-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-brand-blue-600" />
                </div>
                <span className="text-sm font-medium text-brand-blue-900">Total Attempts</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-3xl font-bold text-brand-blue-900">{stats.total}</span>
                  <span className="text-sm text-brand-blue-700 mb-1">attempts</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-blue-700">Pass Rate</span>
                  <span className="text-brand-blue-900 font-semibold">
                    {stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* Passed Attempts Card */}
            <div className="bg-gradient-to-br from-brand-green-50 to-brand-emerald-50 border border-brand-green-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-brand-green-100 rounded-full opacity-40 -translate-x-4 translate-y-4"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-brand-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-brand-green-600" />
                </div>
                <span className="text-sm font-medium text-brand-green-900">Passed Attempts</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-3xl font-bold text-brand-green-900">{stats.passed}</span>
                  <span className="text-sm text-brand-green-700 mb-1">/ {stats.total}</span>
                </div>

                <div className="relative">
                  <div className="h-2 bg-brand-green-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-brand-green-400 to-brand-green-600 rounded-full transition-all duration-700"
                      style={{ width: `${stats.total > 0 ? (stats.passed / stats.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Best Score Card */}
            <div className="bg-gradient-to-br from-brand-purple-50 to-brand-pink-50 border border-brand-purple-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-brand-purple-100 rounded-full opacity-40"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-brand-purple-100 rounded-lg">
                  <Trophy className="w-5 h-5 text-brand-purple-600" />
                </div>
                <span className="text-sm font-medium text-brand-purple-900">Best Score</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-3xl font-bold text-brand-purple-900">{Math.round(stats.bestScore)}</span>
                  <span className="text-lg text-brand-purple-700 mb-1">%</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-purple-700">Average</span>
                  <span className="text-brand-purple-900 font-semibold">{Math.round(stats.averageScore)}%</span>
                </div>
              </div>
            </div>

            {/* Time Invested Card */}
            <div className="bg-gradient-to-br from-brand-orange-50 to-brand-yellow-50 border border-brand-orange-200 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute top-2 right-2 w-12 h-12 bg-brand-orange-100 rounded-full opacity-50"></div>

              <div className="flex items-center gap-3 mb-3 relative z-10">
                <div className="p-2 bg-brand-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-brand-orange-600" />
                </div>
                <span className="text-sm font-medium text-brand-orange-900">Time Invested</span>
              </div>

              <div className="relative z-10">
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-2xl font-bold text-brand-orange-900">{formatTime(stats.overallTimeSpent)}</span>
                </div>

                <div className="text-sm text-brand-orange-700">
                  Total time across {stats.total} attempts
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Exam Attempts List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Exam Attempts Details</h2>
              <p className="text-sm text-gray-500 mt-1">View detailed breakdown of each exam attempt</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search attempts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                />
              </div>
              <SimpleSelect
                value={filterStatus}
                onChange={(value) => setFilterStatus((value || 'all') as string)}
                options={filterStatusOptions}
                isSearchable={false}
              />
              <SimpleSelect
                value={sortBy}
                onChange={(value) => setSortBy((value || 'attempt_number') as string)}
                options={sortByOptions}
                isSearchable={false}
              />
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {sortedAttempts.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No exam attempts found</p>
            </div>
          ) : (
            sortedAttempts.map((attempt) => {
              const isExpanded = expandedAttempts.includes(attempt.attempt_id);
              // Abandoned / in-progress attempts were never scored, so don't label them Failed
              const attemptStatus = attempt.status === 'ABANDONED'
                ? { label: 'Abandoned', badge: 'bg-amber-100 text-amber-700', circle: 'bg-amber-100' }
                : attempt.status === 'IN_PROGRESS'
                ? { label: 'In Progress', badge: 'bg-blue-100 text-blue-700', circle: 'bg-blue-100' }
                : attempt.passed
                ? { label: 'Passed', badge: 'bg-green-100 text-green-700', circle: 'bg-green-100' }
                : { label: 'Failed', badge: 'bg-red-100 text-red-700', circle: 'bg-red-100' };

              return (
                <div key={attempt.attempt_id} className="transition-all duration-200">
                  {/* Attempt Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleAttemptExpansion(attempt.attempt_id)}
                  >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex items-center justify-center w-12 h-12 rounded-full ${
                        attemptStatus.circle
                      }`}>
                        {attempt.passed ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : attemptStatus.label === 'Failed' ? (
                          <XCircle className="w-6 h-6 text-red-600" />
                        ) : (
                          <AlertTriangle className="w-6 h-6 text-amber-600" />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-base font-semibold text-gray-900">
                            Attempt # {attempt.attempt_number}
                          </h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${attemptStatus.badge}`}>
                            {attemptStatus.label}
                          </span>
                          <span className="text-lg font-bold text-gray-900">
                            {attempt.percentage}%
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{formatTime(attempt.time_spent_seconds)}</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{formatDate(attempt.started_at)}</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Target className="w-3.5 h-3.5" />
                            <span>{attempt.score} points</span>
                          </span>
                        </div>
                      </div>

                      <button className="p-2 hover:bg-gray-100 rounded-lg">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content - Questions and Answers */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Question Breakdown
                    </h4>

                    {!attempt.questions || attempt.questions.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No question details available for this attempt</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {attempt.questions.map((question, index) => {
                          const answer = getQuestionAnswer(question, attempt.answers || []);
                          const isCorrect = answer?.is_correct || false;

                        return (
                          <div
                            key={question.question_id}
                            className={`border rounded-lg p-4 bg-white ${
                              isCorrect ? 'border-green-200' : 'border-red-200'
                            }`}
                          >
                            {/* Question Header */}
                            <div className="flex items-start gap-3 mb-3">
                              <div className="flex-shrink-0 mt-0.5">
                                {isCorrect ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-600" />
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs font-semibold text-gray-700">Question {index + 1}</span>
                                  <span className="text-xs text-gray-500">
                                    ({answer?.points_earned || 0}/{answer?.points_possible || question.points} pts)
                                  </span>
                                  <span className="text-xs text-gray-600 px-2 py-0.5 bg-gray-100 rounded">
                                    {question.question_type.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-900 leading-relaxed">{question.question_text}</p>
                              </div>
                            </div>

                            {/* Options (if applicable) */}
                            {question.options && question.options.length > 0 && (
                              <div className="ml-7 mb-3 space-y-1.5">
                                {/* Show "Learner not answered" if no option was selected */}
                                {(!answer?.selected_option_ids || answer.selected_option_ids.length === 0) && (
                                  <div className="p-2.5 rounded-md text-xs bg-yellow-50 border border-yellow-200 mb-2">
                                    <span className="font-medium text-yellow-700">Learner not answered</span>
                                  </div>
                                )}
                                {question.options.map((option) => {
                                  const isSelected = answer?.selected_option_ids?.includes(option.option_id);

                                  // Only the learner's pick is coloured (green right / red wrong); the
                                  // correct answer itself is shown in the Correct Answer box below
                                  return (
                                    <div
                                      key={option.option_id}
                                      className={`p-2.5 rounded-md text-xs ${
                                        isSelected && option.is_correct
                                          ? 'bg-green-50 border border-green-200'
                                          : isSelected
                                          ? 'bg-red-50 border border-red-200'
                                          : 'bg-gray-50 border border-gray-200'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isSelected && (
                                          <span className="text-xs font-medium text-gray-700 px-1.5 py-0.5 bg-white rounded">Your answer</span>
                                        )}
                                        <span className="text-gray-900">{option.option_text}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* User Answer Display - For Text Answer Questions */}
                            {(() => {
                              // Skip if this is a multiple choice question with options (already shown above)
                              if (question.options && question.options.length > 0) {
                                return null;
                              }

                              let displayAnswer = '';
                              let isNotAnswered = false;

                              // First check question.answer_text (from backend question_attempts)
                              const answerText = question.answer_text;
                              if (answerText !== undefined && answerText !== null) {
                                if (typeof answerText === 'string') {
                                  displayAnswer = answerText.trim();
                                  if (!displayAnswer) isNotAnswered = true;
                                } else if (Array.isArray(answerText) && answerText.length > 0) {
                                  displayAnswer = answerText.join(', ');
                                } else {
                                  displayAnswer = JSON.stringify(answerText);
                                }
                              }
                              // Fallback to answer.answer_text
                              else if (answer?.answer_text !== undefined && answer.answer_text !== null) {
                                if (typeof answer.answer_text === 'string') {
                                  displayAnswer = answer.answer_text.trim();
                                  if (!displayAnswer) isNotAnswered = true;
                                } else if (Array.isArray(answer.answer_text) && answer.answer_text.length > 0) {
                                  displayAnswer = answer.answer_text.join(', ');
                                } else {
                                  displayAnswer = JSON.stringify(answer.answer_text);
                                }
                              }
                              // Fallback to answer.text_answer
                              else if (answer?.text_answer) {
                                displayAnswer = answer.text_answer;
                              }
                              // No answer found at all - learner didn't answer
                              else {
                                isNotAnswered = true;
                              }

                              // Show "Learner not answered" for null/empty answers
                              if (isNotAnswered) {
                                return (
                                  <div className="ml-7 mb-3">
                                    <div className="text-xs p-2.5 rounded-md border bg-yellow-50 border-yellow-200">
                                      <span className="font-medium text-yellow-700">Learner not answered</span>
                                    </div>
                                  </div>
                                );
                              }

                              // Only render if we have an answer to display
                              if (displayAnswer) {
                                return (
                                  <div className="ml-7 mb-3">
                                    <div className={`text-xs p-2.5 rounded-md border ${
                                      isCorrect
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-red-50 border-red-200'
                                    }`}>
                                      <span className="font-semibold text-gray-700">
                                        {isCorrect ? 'Learner Answer (Correct): ' : 'Learner Answer (Incorrect): '}
                                      </span>
                                      <span className={`font-medium ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                        {displayAnswer}
                                      </span>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}

                            {/* Correct Answer - when incorrect, and always for text answers (expected keywords) */}
                            {question.correct_answer !== undefined && question.correct_answer !== null &&
                              (!isCorrect || question.question_type === 'TEXT_ANSWER') && (
                              <div className="ml-7 mb-3">
                                <div className="text-xs p-2.5 bg-green-50 rounded-md border border-green-200">
                                  <span className="font-semibold text-gray-700">Correct Answer: </span>
                                  {Array.isArray(question.correct_answer) ? (
                                    <div className="mt-2">
                                      <span className="text-gray-600 text-xs block mb-1">Expected keywords/criteria:</span>
                                      <ul className="list-disc list-inside space-y-1">
                                        {question.correct_answer.map((item: string, idx: number) => (
                                          <li key={idx} className="text-green-700 font-medium">{item}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : (
                                    <span className="font-medium text-green-700">{formatAnswer(question.correct_answer)}</span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Explanation */}
                            {question.explanation && (
                              <div className="ml-7 mt-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                                <p className="text-xs text-blue-900 font-semibold mb-1">Explanation</p>
                                <p className="text-xs text-blue-800 leading-relaxed">{question.explanation}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>
      </div>

      {/* Departments Modal */}
      {userInfo && (
        <Modal
          isOpen={showDepartmentsModal}
          onClose={() => setShowDepartmentsModal(false)}
          title="Department Details"
          subtitle={`${userInfo.name}`}
          size="md"
          maxHeight="80vh"
          footer={
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowDepartmentsModal(false)}
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {(userInfo.departments || (userInfo.department ? [userInfo.department] : [])).map((dept, idx) => (
              <div key={`dept-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{dept}</h3>
                  </div>
                  <Building className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Roles Modal */}
      {userInfo && (
        <Modal
          isOpen={showRolesModal}
          onClose={() => setShowRolesModal(false)}
          title="Role Details"
          subtitle={`${userInfo.name}`}
          size="md"
          maxHeight="80vh"
          footer={
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowRolesModal(false)}
                className="px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {(userInfo.roles || (userInfo.role ? [userInfo.role] : [])).map((role, idx) => (
              <div key={`role-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{role}</h3>
                  </div>
                  <Briefcase className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Edit Tracking Settings Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => !editLoading && setShowEditModal(false)}
        title="Edit Tracking Settings"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowEditModal(false)}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={editLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-indigo-600 rounded-lg hover:bg-brand-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {editLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Mandatory</p>
              <p className="text-xs text-gray-500">Mark this assignment as mandatory</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.is_mandatory}
                onChange={(e) => setEditForm(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-indigo-600" />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Deadline Date</label>
            <p className="text-xs text-gray-500 mb-2">Must be today or a future date</p>
            <input
              type="date"
              value={editForm.deadline_date}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setEditForm(prev => ({ ...prev, deadline_date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-indigo-500 focus:border-brand-indigo-500"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Send Notification</p>
              <p className="text-xs text-gray-500">Turning off also disables notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.send_notification}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEditForm(prev => ({
                    ...prev,
                    send_notification: checked,
                    ...(checked ? {} : { enable_automatic_reminder: false, reminder_frequency_days: '' as any }),
                  }));
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-indigo-600" />
            </label>
          </div>

          <div className={`flex items-center justify-between ${!editForm.send_notification ? 'opacity-50' : ''}`}>
            <div>
              <p className="text-sm font-medium text-gray-900">Automatic Reminder</p>
              <p className="text-xs text-gray-500">Must be enabled to receive the deadline reminder (sent 1 day before the due date) and periodic assignment reminders at the set frequency.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.enable_automatic_reminder}
                disabled={!editForm.send_notification}
                onChange={(e) => setEditForm(prev => ({ ...prev, enable_automatic_reminder: e.target.checked, ...(!e.target.checked ? { reminder_frequency_days: '' as any } : {}) }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-indigo-600 disabled:cursor-not-allowed" />
            </label>
          </div>

          <div className={`${!editForm.enable_automatic_reminder || !editForm.send_notification ? 'opacity-50' : ''}`}>
            <label className="block text-sm font-medium text-gray-900 mb-1">Reminder Frequency (days)</label>
            <p className="text-xs text-gray-500 mb-2">Must be greater than 0</p>
            <input
              type="number"
              min={1}
              max={100}
              placeholder="Enter days (1-100)"
              value={editForm.reminder_frequency_days}
              disabled={!editForm.enable_automatic_reminder || !editForm.send_notification}
              onChange={(e) => setEditForm(prev => ({ ...prev, reminder_frequency_days: e.target.value === '' ? '' as any : parseInt(e.target.value) || '' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-indigo-500 focus:border-brand-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </Modal>

      {/* Full Title Modal */}
      <Modal
        isOpen={showTitleModal}
        onClose={() => setShowTitleModal(false)}
        title="Certification Title"
        size="md"
      >
        <div className="p-2">
          <p className="text-sm sm:text-base text-gray-800 break-words whitespace-pre-wrap">
            {certificationInfo?.title || 'Certification Exam'}
          </p>
        </div>
      </Modal>

      {/* Revoke Certification Confirmation Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => !revokeLoading && setShowRevokeModal(false)}
        title="Revoke Certification Access"
        size="sm"
        closeOnOverlayClick={!revokeLoading}
        footer={
          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-2 sm:gap-3">
            <button
              onClick={() => setShowRevokeModal(false)}
              disabled={revokeLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 border-2 border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeCertification}
              disabled={revokeLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {revokeLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Revoking...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Yes, Revoke Certification
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="flex flex-col items-center sm:items-start sm:flex-row gap-3 sm:gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to revoke this certification for <span className="font-semibold text-gray-900">{userInfo?.name || 'this user'}</span>?
            </p>
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs sm:text-sm text-red-700 font-medium mb-1">This will permanently delete:</p>
              <ul className="text-xs sm:text-sm text-red-600 list-disc list-inside space-y-0.5">
                <li>Certification tracking & progress</li>
                <li>All exam attempts</li>
                <li>Issued certificates</li>
              </ul>
            </div>
            <p className="mt-2 text-xs text-gray-500">This action cannot be undone.</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
